import React from 'react';

import { LUMIO_MOBILE_ANIM } from '../../../../theme/tokens';
// Glow colours come from tokens.raw (RAW hex): they are baked into a data-URI SVG
// (waveTileImage), and var(--ows-…) theme tokens do NOT resolve inside a data URI.
import { LUMIO_MOBILE as LUMIO_RAW } from '../../../../theme/tokens.raw';
import { BOTTOM_WAVE_TILES, waveTileImage } from '../../../../layouts/MainLayout/components/LumioMobile/waves';
import { Star, StarField, WaveBand, WaveLayer } from './LumioWaves.styles';

/**
 * The Lumio backdrop for the content-generating card — a premium touch composed of
 * two purely-decorative (aria-hidden) layers behind the content:
 *  - glow waves: the mobile-overlay wave GEOMETRY (`waves.ts`) + brand glow colours,
 *    three blurred parallax layers screen-blending up from the bottom. Each layer is
 *    a TILED data-URI wave drifted via `background-position` (NOT a 400%-wide
 *    transform layer — that flickers on Safari, see waves.ts / LumioWaves.styles);
 *  - shooting stars: faint white→cyan streaks darting up-right over the right side.
 * Both honour `prefers-reduced-motion` (waves hold a static frame; stars hide).
 */
type LayerDef = {
  key: string;
  img: string;
  durationMs: number;
  reverse?: boolean;
  blur: string;
  opacity: number;
  delayMs?: number;
};

/** Banner-only speed-up: the mobile overlay's waves drift slowly (ambient); on the
 *  home card we run them faster for a livelier "working" feel. Applied locally so the
 *  shared LUMIO_MOBILE_ANIM tokens (and the mobile overlay) are untouched. */
const WAVE_SPEEDUP = 6;

const LAYERS: LayerDef[] = [
  {
    key: 'violet',
    img: waveTileImage(BOTTOM_WAVE_TILES.violet, LUMIO_RAW.glowViolet, 'down'),
    durationMs: LUMIO_MOBILE_ANIM.waveSlowMs / WAVE_SPEEDUP,
    reverse: true,
    blur: LUMIO_MOBILE_ANIM.glowBlurSoft,
    opacity: 0.6,
    delayMs: 4000,
  },
  {
    key: 'magenta',
    img: waveTileImage(BOTTOM_WAVE_TILES.magenta, LUMIO_RAW.glowMagenta, 'down'),
    durationMs: LUMIO_MOBILE_ANIM.waveMidMs / WAVE_SPEEDUP,
    blur: LUMIO_MOBILE_ANIM.glowBlurSoft,
    opacity: 0.5,
    delayMs: 7000,
  },
  {
    key: 'cyan',
    img: waveTileImage(BOTTOM_WAVE_TILES.cyan, LUMIO_RAW.glowCyan, 'down'),
    durationMs: LUMIO_MOBILE_ANIM.waveFastMs / WAVE_SPEEDUP,
    reverse: true,
    blur: LUMIO_MOBILE_ANIM.glowBlurSoft,
    opacity: 0.5,
    delayMs: 12000,
  },
];

const Layer = ({ def }: { def: LayerDef }): React.ReactElement => (
  <WaveLayer
    $img={def.img}
    $dur={def.durationMs}
    $reverse={def.reverse}
    $blur={def.blur}
    $opacity={def.opacity}
    $delay={def.delayMs}
  />
);

/** Streaks across the right side: varied start, length, speed, stagger + faintness
 *  so they read as occasional shooting stars rather than a steady stream. */
type StarDef = { key: string; top: string; left: string; len: number; durMs: number; delayMs: number; peak: number };
const STARS: StarDef[] = [
  { key: 's1', top: '72%', left: '6%', len: 56, durMs: 5200, delayMs: 0, peak: 0.42 },
  { key: 's2', top: '88%', left: '30%', len: 42, durMs: 6400, delayMs: 1800, peak: 0.3 },
  { key: 's3', top: '58%', left: '50%', len: 64, durMs: 4600, delayMs: 3200, peak: 0.38 },
  { key: 's4', top: '94%', left: '16%', len: 36, durMs: 7000, delayMs: 4600, peak: 0.28 },
  { key: 's5', top: '80%', left: '64%', len: 50, durMs: 5600, delayMs: 2500, peak: 0.34 },
];

export const LumioWaves = (): React.ReactElement => (
  <>
    <WaveBand aria-hidden="true">
      {LAYERS.map((def) => (
        <Layer key={def.key} def={def} />
      ))}
    </WaveBand>
    <StarField aria-hidden="true">
      {STARS.map((s) => (
        <Star key={s.key} $top={s.top} $left={s.left} $len={s.len} $dur={s.durMs} $delay={s.delayMs} $peak={s.peak} />
      ))}
    </StarField>
  </>
);

export default LumioWaves;
